import React from 'react';
import {
  Typography,
  Chip,
  Stack,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  Hub as HubIcon,
  AccountTree as AccountTreeIcon,
  Timeline as TimelineIcon,
  Update as UpdateIcon,
} from '@mui/icons-material';
import { GraphStats } from '../../../types/graph.types';

interface GraphStatsCardProps {
  stats: GraphStats;
  sx?: any;
}

/**
 * GraphStatsCard Component
 * 
 * Displays key statistics about the knowledge graph.
 * Shows node counts, edge counts, and other metrics.
 * 
 * Features:
 * - Compact display for toolbar integration
 * - Tooltips with detailed breakdowns
 * - Live update indicators
 * - Color coding by data type
 */
const GraphStatsCard: React.FC<GraphStatsCardProps> = ({ stats, sx }) => {
  // Format large numbers
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  // Format time ago
  const formatTimeAgo = (date: Date): string => {
    const seconds = Math.floor((new Date().getTime() - new Date(date).getTime()) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return new Date(date).toLocaleDateString();
  };

  // Get dominant node type
  const getDominantNodeType = (): { type: string; count: number } => {
    if (!stats?.nodesByType) return { type: 'none', count: 0 };
    const entries = Object.entries(stats.nodesByType) as [string, number][];
    if (entries.length === 0) return { type: 'none', count: 0 };
    
    const sorted = entries.sort((a, b) => b[1] - a[1]);
    return { type: sorted[0][0], count: sorted[0][1] };
  };

  // Get dominant edge type
  const getDominantEdgeType = (): { type: string; count: number } => {
    if (!stats?.edgesByType) return { type: 'none', count: 0 };
    const entries = Object.entries(stats.edgesByType) as [string, number][];
    if (entries.length === 0) return { type: 'none', count: 0 };
    
    const sorted = entries.sort((a, b) => b[1] - a[1]);
    return { type: sorted[0][0], count: sorted[0][1] };
  };

  const dominantNode = getDominantNodeType();
  const dominantEdge = getDominantEdgeType();

  // Build tooltip content for nodes
  const buildNodeTooltip = (): string => {
    if (!stats?.nodesByType) return 'Node Breakdown:\nNo data available';
    const lines = Object.entries(stats.nodesByType)
      .map(([type, count]) => `${type}: ${formatNumber(count as number)}`)
      .join('\n');
    return `Node Breakdown:\n${lines}`;
  };

  // Build tooltip content for edges
  const buildEdgeTooltip = (): string => {
    if (!stats?.edgesByType) return 'Edge Breakdown:\nNo data available';
    const lines = Object.entries(stats.edgesByType)
      .map(([type, count]) => `${type}: ${formatNumber(count as number)}`)
      .join('\n');
    return `Edge Breakdown:\n${lines}`;
  };

  return (
    <Paper
      elevation={0}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 8,
        paddingBottom: 8,
        backgroundColor: 'transparent',
        ...sx,
      }}
    >
      {/* Nodes Count */}
      <Tooltip title={buildNodeTooltip()} arrow>
        <Stack direction="row" spacing={1} alignItems="center">
          <HubIcon fontSize="small" color="primary" />
          <div>
            <Typography variant="h6" component="span">
              {formatNumber(stats?.nodeCount || 0)}
            </Typography>
            <Typography variant="caption" color="text.secondary" style={{ marginLeft: 4 }}>
              nodes
            </Typography>
          </div>
          {dominantNode.count > 0 && (
            <Chip
              label={dominantNode.type}
              size="small"
              variant="outlined"
              style={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Stack>
      </Tooltip>

      <div style={{ width: 1, height: 24, backgroundColor: 'rgba(0,0,0,0.12)' }} />

      {/* Edges Count */}
      <Tooltip title={buildEdgeTooltip()} arrow>
        <Stack direction="row" spacing={1} alignItems="center">
          <AccountTreeIcon fontSize="small" color="primary" />
          <div>
            <Typography variant="h6" component="span">
              {formatNumber(stats?.edgeCount || 0)}
            </Typography>
            <Typography variant="caption" color="text.secondary" style={{ marginLeft: 4 }}>
              edges
            </Typography>
          </div>
          {dominantEdge.count > 0 && (
            <Chip
              label={dominantEdge.type}
              size="small"
              variant="outlined"
              style={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Stack>
      </Tooltip>

      <div style={{ width: 1, height: 24, backgroundColor: 'rgba(0,0,0,0.12)' }} />

      {/* Graph Metrics */}
      <Tooltip 
        title={`Average Degree: ${(stats?.averageDegree || 0).toFixed(2)}\nDensity: ${((stats?.density || 0) * 100).toFixed(2)}%\nComponents: ${stats?.connectedComponents || 0}`}
        arrow
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <TimelineIcon fontSize="small" color="primary" />
          <div>
            <Typography variant="body2">
              {(stats?.averageDegree || 0).toFixed(1)} avg
            </Typography>
            <Typography variant="caption" color="text.secondary">
              degree
            </Typography>
          </div>
        </Stack>
      </Tooltip>

      <div style={{ width: 1, height: 24, backgroundColor: 'rgba(0,0,0,0.12)' }} />

      {/* Last Updated */}
      <Tooltip title={`Last updated: ${stats?.lastUpdated ? new Date(stats.lastUpdated).toLocaleString() : 'Never'}`} arrow>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <UpdateIcon fontSize="small" color="action" />
          <Typography variant="caption" color="text.secondary">
            {stats?.lastUpdated ? formatTimeAgo(stats.lastUpdated) : 'Never'}
          </Typography>
        </Stack>
      </Tooltip>
    </Paper>
  );
};

export default GraphStatsCard;